import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
interface Indexable {
  [key: string]: any;
  [key: symbol]: any;
}

const untilDestroyedSymbol = Symbol('untilDestroyed');

/**
 * RxJS operator that unsubscribe from observables on destroy.
 * Code forked from https://github.com/NetanelBasal/ngx-take-until-destroy
 *
 * IMPORTANT: Add the `untilDestroyed` operator as the last one to prevent leaks with intermediate observables in the
 * operator chain.
 *
 * @param instance The parent Angular component or object instance.
 * @param destroyMethodName The method to hook on (default: 'ngOnDestroy').
 * @example
 * ```
 * import { untilDestroyed } from '@app/core';
 *
 * @Component({
 * selector: 'app-example',
 *   templateUrl: './example.component.html'
 * })
 * export class ExampleComponent implements OnInit, OnDestroy {
 *   ngOnInit() {
 *     interval(1000)
 *       .pipe(untilDestroyed(this))
 *       .subscribe(val => console.log(val));
 *   }
 *
 *   // This method must be present, even if empty.
 *   ngOnDestroy() {
 *     // To protect you, an error will be thrown if it doesn't exist.
 *   }
 * }
 * ```
 */

export function untilDestroyed<T extends Indexable>(instance: T, destroyMethodName: string = 'ngOnDestroy') {
  return <U>(source: Observable<U>) => {
    const mutableInstance = instance as Indexable;

    const originalDestroy = mutableInstance[destroyMethodName];
    const hasDestroyFunction = typeof originalDestroy === 'function';

    if (!hasDestroyFunction) {
      throw new Error(`${instance.constructor.name} is using untilDestroyed but doesn't implement ${destroyMethodName}`);
    }

    if (!mutableInstance[untilDestroyedSymbol]) {
      mutableInstance[untilDestroyedSymbol] = new Subject();
      mutableInstance[destroyMethodName] = function() {
        if (hasDestroyFunction) {
          originalDestroy.apply(this, arguments);
        }
        mutableInstance[untilDestroyedSymbol].next();
        mutableInstance[untilDestroyedSymbol].complete();
      };
    }

    return source.pipe(takeUntil<U>(mutableInstance[untilDestroyedSymbol]));
  };
}
